import { useEffect, useState } from 'react';
import { PlayerProfile } from '../../shared/analysis/aggregate';
import { AnalysisProgress, api } from '../api';
import { getPlayerName } from '../cookie';
import { formatDuration } from '../utils';
import { ProgressNote } from './profile-charts';
import { canonicalPlayerKey } from '../../shared/analysis/aggregate';

// The roster: everyone the replay corpus knows about. Clicking through opens the full profile.
//
// No preamble — the page is a dashboard, and the totals at the top say what it is. A card is a
// record, not a rating. The three big numbers are what someone has actually done —
// solves, wins, time played — and the three small ones are the raw volume behind it, none of them
// scored good or bad. The one interpretive thing on the card is the play style, which is a
// description rather than a rank: the roster is a set of different games being played well.
//
// Profiles built from a handful of marks are noise dressed as insight, so anyone under the
// threshold is listed but visibly marked as provisional.

const MIN_FACTS = 200;

export interface RosterData {
  progress: AnalysisProgress;
  population: PlayerProfile;
  players: PlayerProfile[];
}

interface Props {
  onBack: () => void;
  onSelect: (key: string) => void;
}

const navClick = (cb: () => void) => (e: React.MouseEvent) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
  e.preventDefault();
  cb();
};

export function PlayersView({ onBack, onSelect }: Props) {
  const [data, setData] = useState<RosterData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const load = () => api.getPlayers().then(d => {
      if (!live) return;
      setData(d);
      // The backfill runs in the background the first time anyone opens this page; keep the
      // numbers moving rather than showing a stale half-corpus until a manual refresh.
      if (d.progress.running) timer = setTimeout(load, 5000);
    }).catch(() => { if (live) setError(true); });
    load();
    return () => { live = false; clearTimeout(timer); };
  }, []);

  if (error) return <p className="error">Failed to load player profiles.</p>;
  if (!data) return <p className="loading">Analyzing replays…</p>;
  // There are no accounts — "you" is whoever the queens_player cookie says you are. Read here
  // rather than in the body so the body stays renderable without a document (the tests).
  return (
    <PlayersBody
      data={data} onBack={onBack} onSelect={onSelect}
      meKey={canonicalPlayerKey(getPlayerName() || '')}
    />
  );
}

export function PlayersBody({ data, onBack, onSelect, meKey }: { data: RosterData; meKey?: string } & Props) {
  const { players, population, progress } = data;
  const solvesOf = (p: PlayerProfile) => p.record?.solves ?? 0;
  const ranked = [...players].sort((a, b) => solvesOf(b) - solvesOf(a) || b.facts - a.facts);
  const totalSolves = players.reduce((a, p) => a + solvesOf(p), 0);

  return (
    <div className="prof-view">
      <div className="prof-header">
        <a className="link-btn" href="/" onClick={navClick(onBack)}>← Puzzles</a>
      </div>

      {/* The totals the cards add up to, so the page opens on the same footing they do: the
          solve count is summed from the cards rather than taken from the attribution corpus,
          which lags it while a backfill runs. Centered, because with the heading gone they are
          the top of the page. */}
      <div className="prof-dashboard">
        <Big label="Solves" value={count(totalSolves)} />
        <Big label="Marks analyzed" value={count(population.facts)} />
      </div>

      <ProgressNote done={progress.done} total={progress.total} running={progress.running} />

      <div className="prof-roster">
        {ranked.map(p => {
          const thin = p.facts < MIN_FACTS;
          return (
            <a
              key={p.key}
              className={`prof-card prof-roster-card${thin ? ' thin' : ''}${meKey && p.key === meKey ? ' me' : ''}`}
              href={`/players/${encodeURIComponent(p.key)}`}
              onClick={navClick(() => onSelect(p.key))}
            >
              <div className="prof-roster-name">
                {p.displayName}
                {meKey && p.key === meKey && <span className="prof-you">you</span>}
                {p.tokens > 1 && <span className="prof-dim"> · {p.tokens} devices</span>}
              </div>
              <div className="prof-roster-big">
                <Big label="Solves" value={count(p.record?.solves)} />
                <Big label="Wins" value={count(p.record?.wins)} />
                <Big label="Time played" value={num(p.record?.timePlayedMs, formatDuration)} />
              </div>

              <div className="prof-roster-small">
                {/* Hand-written Xs plus the ones Auto Xs put down for them: the label is about
                    the board that got filled in, and someone playing with the setting on did not
                    stop eliminating — they stopped clicking. Summed here rather than upstream so
                    `xMarks` stays "marks the player made" for attribution. */}
                <Small label="Xs placed" value={count(p.xMarks + p.autoXMarks)} />
                <Small label="Queens placed" value={count(p.queenMarks)} />
                <Small label="Ratio" value={num(p.xPerQueen, x => x.toPrecision(3))} />
              </div>

              {/* A card is a link, and on a phone there is no hover to discover that with — so
                  the footer says so outright rather than relying on the cursor. */}
              <div className="prof-roster-foot">
                {thin ? (
                  <span className="prof-dim prof-roster-thin">Not enough solves yet for a reliable profile.</span>
                ) : (
                  <span className="prof-tagline style">
                    {p.style.label}
                    {p.highlights[0]?.kind === 'strength' && (
                      <span className="prof-tagline-sep">· {p.highlights[0].label}</span>
                    )}
                  </span>
                )}
                <span className="prof-roster-open">Full stats <span aria-hidden="true">→</span></span>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Every number on a card goes through one of these.
 *
 * A field that is null (no data yet), missing, or NaN has to render as a dash — not throw. The
 * page has twice been blanked by a single `undefined.toFixed` when a server was left running
 * older code than the bundle, and one absent number is not worth losing the roster over. The
 * error boundary in index.tsx is the backstop; this is the part that keeps the rest of the card
 * readable when only one value is missing.
 */
const num = (x: number | null | undefined, fmt: (n: number) => string) =>
  (typeof x === 'number' && Number.isFinite(x) ? fmt(x) : '—');
const count = (x: number | null | undefined) => num(x, n => n.toLocaleString());

function Big({ label, value }: { label: string; value: string }) {
  return (
    <div className="prof-big">
      <div className="prof-big-value">{value}</div>
      <div className="prof-big-label">{label}</div>
    </div>
  );
}

function Small({ label, value }: { label: string; value: string }) {
  return (
    <span className="prof-small">
      <span className="prof-small-value">{value}</span>
      <span className="prof-small-label">{label}</span>
    </span>
  );
}
